'use client'

import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, Check } from 'lucide-react'
import Image from 'next/image'
import CarGallery from '@/components/CarGallery'
import RentToBuyCalculator from '@/components/noleggio/RentToBuyCalculator'
import { RentToBuyServices, RentToBuySpecifications } from '@/components/noleggio/RentToBuySections'
import RentToBuyQuoteModal from '@/components/noleggio/RentToBuyQuoteModal'
import { formatDate } from '@/utils/format-date'
import type { RentToBuyDetailContent } from '@/lib/rent-to-buy-detail-content'

interface Props {
  veicoloData: any
  tipoUtenteListino: string
  content: RentToBuyDetailContent
}

function display(value: unknown, suffix = ''): string | null {
  if (value === null || value === undefined || value === '') return null
  return `${value}${suffix}`
}

function displayKilometres(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  const kilometres = Number(value)
  return Number.isFinite(kilometres)
    ? `KM ${new Intl.NumberFormat('it-IT', { useGrouping: 'always' }).format(kilometres)}`
    : `KM ${String(value)}`
}

export default function RentToBuyDetailClient({ veicoloData, tipoUtenteListino, content }: Props) {
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    })
    return () => cancelAnimationFrame(frame)
  }, [veicoloData.slug])

  const [km, setKm] = useState(String(content.initialAnnualKm))
  const [durata, setDurata] = useState(String(content.initialDurationMonths))
  const [anticipo, setAnticipo] = useState(String(content.initialDownPayment))
  const [cauzione, setCauzione] = useState(String(content.initialDeposit))
  const [selectedServices, setSelectedServices] = useState<{ cprownum: number; descrizione_nclad: string; importo: string }[]>([])
  const [availableServices, setAvailableServices] = useState<{ cprownum: number; codice_nclad: string; descrizione_nclad: string; importo: string; obbligatorio_nclad: number }[]>([])
  const handleSelectedServicesChange = useCallback((services: { cprownum: number; descrizione_nclad: string; importo: string }[]) => setSelectedServices(services), [])
  const [quoteModalOpen, setQuoteModalOpen] = useState(false)
  const images = Array.isArray(veicoloData.images) ? veicoloData.images : []
  const title = veicoloData.modello || veicoloData.marca
  const chips = [
    displayKilometres(veicoloData.km),
    veicoloData.immatricolazione ? display(formatDate(veicoloData.immatricolazione)) : null,
    display(veicoloData.alimentazione),
    display(
        [
          veicoloData.localita?.trim(),
          veicoloData.provincia?.trim()
              ? `(${veicoloData.provincia.trim()})`
              : null
        ]
            .filter(Boolean)
            .join(' ')
    ),
    display(veicoloData.articolo2),
  ].filter((value): value is string => Boolean(value))

  return (
    <main className="bg-white pb-16 pt-6 text-[#10223a] lg:pt-8">
      <div className="mx-auto max-w-[1440px] px-5 lg:px-20">
        <Link href="/rent-to-buy" className="inline-flex items-center gap-2 text-sm font-bold text-[#005aa9] hover:underline">
          <ArrowLeft size={16} aria-hidden="true" /> {content.backLabel}
        </Link>
        <header className="mt-5 mb-6 lg:mb-8">
          {veicoloData.marca && <p className="text-base font-bold text-[#005aa9] lg:text-lg">{veicoloData.marca}</p>}
          <h1 className="mt-1 text-2xl font-extrabold leading-[1.15] tracking-[-0.02em] lg:text-4xl">{title || 'Veicolo Rent to Buy'}</h1>
          {veicoloData.supp && veicoloData.supp !== veicoloData.modello && (
            <p className="mt-2 text-sm text-[#53647a]">{veicoloData.supp}</p>
          )}
          {chips.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-2" aria-label="Caratteristiche principali">
              {chips.map((chip, index) => (
                <li key={`${chip}-${index}`} className="rounded-full border border-[#dce4ee] bg-white px-3 py-1.5 text-[13px] font-bold">{chip}</li>
              ))}
            </ul>
          )}
        </header>

        <div className="grid items-start gap-y-7 lg:grid-cols-[minmax(0,736px)_minmax(0,520px)] lg:gap-x-6">
          <div className="flex min-w-0 flex-col gap-7 lg:gap-8">
            <CarGallery photos={images} articolo={veicoloData.articolo ?? veicoloData.articolo2} variant="rent-to-buy" />
            {content.rentToBuyHighlights.map((highlight, index) => (
                <aside key={`${highlight.title}-${index}`} className="flex items-center gap-3 rounded-[18px] bg-gradient-to-br from-[#07137e] to-[#005aa9] p-5 shadow-[0_8px_22px_rgba(5,15,51,0.05)]">
                  <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-white/15 text-white"><Check size={15} strokeWidth={3} aria-hidden="true" /></span>
                  <div className="flex min-w-0 flex-col gap-1 leading-[1.4]">
                    <h2 className="text-base font-extrabold text-white">{highlight.title}</h2>
                    <p className="text-[13px] font-medium text-white/80">{highlight.description}</p>
                  </div>
                </aside>
            ))}
            <section aria-labelledby="why-choose-rent-to-buy">
              <h2 id="why-choose-rent-to-buy" className="text-[22px] font-extrabold text-[#10223a] lg:text-2xl">{content.whyChooseTitle}</h2>
              <ul className="mt-3 grid grid-cols-2 gap-2.5 lg:mt-3.5 lg:gap-4">
                {content.whyChooseBenefits.map((benefit, index) => (
                  <li key={`${benefit}-${index}`} className="flex min-h-28 min-w-0 flex-col items-start gap-2 rounded-[18px] bg-white p-3.5 text-[13px] font-bold leading-[1.4] text-[#10223a] shadow-[0_8px_22px_rgba(5,15,51,0.05)] lg:min-h-0 lg:flex-row lg:gap-3 lg:p-5 lg:text-[15px]">
                    <Image src="/assets/rent-to-buy/icon-benefit-check.svg" width={22} height={22} alt="" aria-hidden="true" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </section>
            <RentToBuyServices content={content} />
          </div>
          <div className="min-w-0 self-start lg:sticky lg:top-6">
            <RentToBuyCalculator
              veicoloData={veicoloData}
              tipoUtenteListino={tipoUtenteListino}
              content={content}
              onChangeDurata={setDurata}
              onChangeKm={setKm}
              onChangeAnticipo={setAnticipo}
              onOpenQuote={() => setQuoteModalOpen(true)}
              onSelectedServicesChange={handleSelectedServicesChange}
              onAvailableServicesChange={setAvailableServices}
              onDepositChange={setCauzione}
            />
          </div>
        </div>

        <div className="mt-7 lg:mt-10">
          <RentToBuySpecifications vehicle={veicoloData} content={content} />
        </div>
        <RentToBuyQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} vehicle={veicoloData} km={km} durata={durata} anticipo={anticipo} cauzione={cauzione} availableServices={availableServices} selectedServices={selectedServices} content={content} />
      </div>
    </main>
  )
}
